import { InboundSwitchTracker } from '@ovotech/energy-cx';
import { Stack } from '@ovotech/nebula';
import { useAccountContext } from '@ovotech/ui-tools';

import { useQueryClient } from 'react-query';
import React, { useEffect, useState } from 'react';
import { Route, useLocation } from 'react-router-dom-v5';
import {
  LinkReferrer,
  redirectToMobileStore,
} from '@/src/utils/mobileStoreRedirect';
import {
  PageWithNavigation,
  PageWithoutNavigationAndSectionPadding,
  Section,
  SwitchWithNotFound,
} from '@/src/components';
import { ONBOARDING } from '@/src/constants/analytics-subjects';

import { ROUTE_HOME } from '@/src/constants/routes';
import {
  haveAllSuppliesBeenActive,
  areAllSuppliesPastWindow,
} from '@/src/utils/supplyPoints';
import SiteLoading from '@/src/components/Wrapper/SiteLoading';
import { useSupplyPointsQuery } from '@/src/api/kapi/use-supply-points-query';
import { useMarketingPreferencesQuery } from '@/src/api/kapi/use-marketing-preferences-query';
import { EmailConsentCard } from '@/src/components/ActionCard/components/EmailConsentCard';
import { MarketingChannel } from '@/src/constants/enums';
import { Status, StatusCard } from './components/StatusCard';
import styled from 'styled-components';
import {
  beyondAccountStatusQueryKey,
  beyondAccountStatusQueryFn,
} from '@/src/api/use-beyond-account-status';
import { useBeyondAccountStatusQuery } from '@/src/api';
import { PAYMHomePage } from './PAYMHomePage';
import { useAccountBillingType } from '@/src/hooks/useAccountBillingType';
import { PAYGHomePage } from './PAYGHomePage';

const InboundSwitchTrackerWrapper = styled.div`
  margin-top: ${({ theme }) => theme.space[8]};
`;

const Energy = () => {
  const marketingPrefsQuery = useMarketingPreferencesQuery();
  const beyondStatusQuery = useBeyondAccountStatusQuery();
  const supplyPointsQuery = useSupplyPointsQuery();
  const accountBillingType = useAccountBillingType();

  const [isEmailConsentCardDismissed, setIsEmailConsentCardDismissed] =
    useState<boolean>(false);

  const [activationStatus, setActivationStatus] = useState<Status>(null);

  const isLoading =
    accountBillingType.isLoading ||
    supplyPointsQuery.isLoading ||
    marketingPrefsQuery.isFetching ||
    beyondStatusQuery.isFetching;

  if (isLoading) {
    return (
      <PageWithNavigation title="Home">
        <Section>
          <SiteLoading />
        </Section>
      </PageWithNavigation>
    );
  }

  const isError = supplyPointsQuery.isError || accountBillingType.isError;

  if (isError) {
    return (
      <PageWithNavigation title="Home">
        <Section />
      </PageWithNavigation>
    );
  }

  const supplies = supplyPointsQuery.data?.account.accountSupplyPoints ?? [];
  const isEmailMarketingOn =
    !marketingPrefsQuery.isError &&
    !!(marketingPrefsQuery.data ?? []).find(
      channel => channel === MarketingChannel.EMAIL,
    );

  const showEmailConsentCard =
    !isEmailMarketingOn && !isEmailConsentCardDismissed;

  if (!haveAllSuppliesBeenActive(supplies)) {
    return (
      <PageWithoutNavigationAndSectionPadding
        title="Onboarding"
        analytics={{ name: ONBOARDING }}
      >
        <Stack spaceBetween={8}>
          {showEmailConsentCard && (
            <EmailConsentCard
              onSuccess={() => setActivationStatus('success')}
              onError={() => setActivationStatus('error')}
              onDismiss={() => setIsEmailConsentCardDismissed(true)}
              existingPreferences={marketingPrefsQuery.data ?? []}
              isFetching={marketingPrefsQuery.isFetching}
            />
          )}
          <StatusCard status={activationStatus} />
        </Stack>
        <InboundSwitchTrackerWrapper data-testid="orion-wrapper-inbound-switch-tracker">
          <InboundSwitchTracker />
        </InboundSwitchTrackerWrapper>
      </PageWithoutNavigationAndSectionPadding>
    );
  }

  const allSuppliesArePastWindow = areAllSuppliesPastWindow(supplies);

  return accountBillingType.data.type === 'PAYG' ? (
    <PAYGHomePage />
  ) : (
    <PAYMHomePage
      allSuppliesArePastWindow={allSuppliesArePastWindow}
      beyondAccountStatusData={beyondStatusQuery.data}
    />
  );
};

const HomePage = () => {
  const { accountId } = useAccountContext();
  const queryClient = useQueryClient();

  // Prefetch beyond account status on home page load as it's used in multiple places across the app and likely to be needed soon after landing on the home page
  useEffect(() => {
    if (accountId) {
      queryClient.prefetchQuery(beyondAccountStatusQueryKey(accountId), () =>
        beyondAccountStatusQueryFn(accountId),
      );
    }
  }, [accountId, queryClient]);

  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);
  const referrer = searchParams.get(LinkReferrer.BASE);

  // Redirect to mobile-store if on a mobile device and OVO app not installed
  if (referrer === LinkReferrer.TYPE_EMAIL && redirectToMobileStore()) {
    return <SiteLoading />;
  }

  return (
    <SwitchWithNotFound>
      <Route exact path={ROUTE_HOME} component={Energy} />
    </SwitchWithNotFound>
  );
};

export default HomePage;
